import { z } from "zod";
import { handoffPrioritySchema } from "./handoff";
import { employeeRoleSchema } from "./roles";
import { coachNowSchema } from "./coach-now";
import {
  meetingPrescriptionOrderSchema,
  prescriptionOrderSchema,
} from "./prescription-cycle";
import {
  prescriptionDecisionSchema,
  webdocControlSchema,
} from "./prescription-choice";
import { labResultCheckSchema } from "./lab-result-check";
import {
  COACH_WELCOME_TEMPLATE_SV,
  renderCoachTemplate,
} from "./coach-template";

// TES-289 / TES-290 — the clinician work-queue workspace. A single, prioritized,
// multi-LANE read surface that AGGREGATES already-built primitives (review items,
// handoffs, behavioral signals, prescription-cycle renewals, the onboarding inbox,
// wellness programs) into one queue per caller role. Read-only projection: the
// underlying services own the state + the actions; this contract is the shape the
// admin renders.
//
// The queue DOES carry patient data: the name + personnummer shorthand every
// row needs to be triaged by patient (TES-466), and — on renewal rows — the
// answers the patient submitted with the renewal, including clinical free text
// (TES-495). It is a role-gated, doctor-scoped read of data the same caller can
// already open on the patient page, and it is never cached (no Cache-Control on
// the route). Said plainly here because this header used to claim "no patient
// PII beyond ids + clinician-authored titles", and a stale invariant is worse
// than an inconvenient one: anything added to this contract must be weighed as
// PHI leaving the owning surface, not waved through on the strength of an old
// sentence.

// ---------------------------------------------------------------------------
// Lanes
// ---------------------------------------------------------------------------

// The union of every lane a coach OR a doctor can see. The server only ever
// returns the lanes relevant to the caller's role (a coach never gets a doctor
// lane and vice-versa); the enum is shared so the admin can label them.
// TES-519 — `reviews_to_approve` is gone with the review queue.
// "all things that are to stay should have their own menu items
// and own badges, all leading to the patient screen with the correct content
// up and a clear cta." Two lanes took its place: `checkins` (the flagged
// weekly check-in the queue used to wrap in a review item) and `labs` (lab
// orders that need the doctor's hand — never a lane before, only a tab).
export const WORK_QUEUE_LANES = [
  // shared
  "handoffs",
  "checkins",
  // TES-915 — the clinician's OWN "Följ upp senare" reminders, once their
  // date has come. Per person, not per role: a reminder is something one
  // clinician promised herself, and nobody else's queue should carry it.
  "follow_ups",
  // coach
  // TES-1189 (TES-1173 point 1) — "Nya patienter": one "Välkomna ny patient"
  // case per patient with a live coach-plan subscription whom no coach has
  // written to yet.
  "new_patients",
  // TES-818 (A-01/CP-23) — kept in the vocabulary, gone from the coach's run
  // list, for the same reason as `program_changes` below. Emelie 2026-09-20:
  // a SINGLE missed check-in must not create coach work; only two completely
  // missed cycles produce a gentle case, and that is `adherence_risk`.
  "missed_checkins",
  "adherence_risk",
  // TES-764 — kept in the vocabulary but NOT in any role's run list, so
  // never projected. The enum value is kept on purpose: `WorkQueueLane` types
  // exhaustive Record tables in admin (labels, paths), and removing it would
  // have been a contract change for something that only has to stop showing
  // up. Dropping it from COACH_WORK_QUEUE_LANES is the whole fix.
  "program_changes",
  // doctor
  "renewals",
  "onboarding",
  "side_effects",
  "medical_alerts",
  // TES-986 (Ö-266) — medical review cases: F-04 answers that route to the
  // doctor on their own, and what a coach sent on with "Skicka till läkare".
  "medical_reviews",
  "labs",
  // TES-1059 (TES-1054 point D) — one item per completion round whose items
  // are all done: "När alla valda delar är klara skapas eller återaktiveras ett enda samlat ärende i Kirills arbetskö."
  "completion_rounds",
  // TES-1149 — one item per doctor's question the patient has answered
  // ("Svar på läkarens fråga"), until the doctor closes it.
  "doctor_question_answers",
  // TES-765 (CP-05) — documented care meetings that lack their Webdoc
  // confirmation after 48 hours. A "Stor kvarstående uppgift" (large
  // outstanding task), not an alarm: the patient's care has already moved on;
  // this is an administrative tail nobody should have to keep in their head.
  "webdoc_confirmations",
  // TES-1233 — a passed meeting with neither documentation nor active
  // handling (meeting-calendar.ts).
  "meetings_to_document",
] as const;
export const workQueueLaneSchema = z.enum(WORK_QUEUE_LANES);
export type WorkQueueLane = z.infer<typeof workQueueLaneSchema>;

// The lanes surfaced to each role. The server filters by this; the admin uses it
// to decide which lanes to render even before data arrives.
// TES-764 (part of Ö-170) — `program_changes` is GONE from the coach's queue.
//
// Emelie 2026-09-17, translated: the programmes are static in the MVP and must
// not change automatically based on patient input; the underlying data may
// remain without creating a coach case. A queue that proposes programme
// changes invites exactly that automation.
//
// The `WellnessProgram` data is untouched; only the projection went away.
//
// TES-818 (Ö-186 follow-up finding) — `missed_checkins` is GONE from the
// coach's queue.
//
// Emelie 2026-09-20, against A-01 and CP-23, translated: a single miss must
// not create coach work; only two completely missed cycles produce a gentle
// "Behöver återkontakt" case. The lane projected ONE `BehavioralSignal` to a
// card headed "Missad incheckning" — exactly that work.
//
// `adherence_risk` remains and is the row that should exist: it fires on
// TWO_CONSECUTIVE_WEEKLY_CHECKINS_MISSED, keeps the priority at `medium` and
// gives one card per patient (TES-751). The signal data is untouched; only
// the projection went away.
//
// TES-1189 — `new_patients` FIRST. A-01 lists the coach's sections in the
// order "Nya patienter, Meddelanden, Veckoincheckningar, Delade reflektioner,
// Överlämningar och Behöver återkontakt".
// TES-1214 — the rest of that order: "Veckoincheckningar" before "Överlämningar".
// Follow-ups have no section of their own in A-01 ("När tiden infaller skapas
// ett vanligt ärende i befintlig arbetskö"), so the lane stays last.
export const COACH_WORK_QUEUE_LANES: readonly WorkQueueLane[] = [
  "new_patients",
  "checkins",
  "handoffs",
  "adherence_risk",
  "follow_ups",
];
// TES-986 — `medical_reviews` FIRST: a RED case ("Brådskande") sits at the
// top of the doctor's list, and stays there until she has opened the
// originals and marked the case reviewed.
export const DOCTOR_WORK_QUEUE_LANES: readonly WorkQueueLane[] = [
  "medical_reviews",
  "medical_alerts",
  // TES-1233 — a missed or unwritten meeting is patient safety, so it sits
  // with the doctor's own open steps, above the administrative Webdoc tail.
  "meetings_to_document",
  "webdoc_confirmations",
  "onboarding",
  "checkins",
  "renewals",
  "side_effects",
  "completion_rounds",
  // TES-1149 — her answer to his question, beside the completions.
  "doctor_question_answers",
  "labs",
  "handoffs",
  "follow_ups",
];

// The item-level kind — a finer discriminator than the lane, so the admin can
// pick an icon / CTA per source. Enumerated for exhaustiveness.
export const WORK_QUEUE_ITEM_KINDS = [
  "handoff",
  // TES-915 — a due CoachFollowUp. Closing the card completes the follow-up.
  "follow_up",
  "checkin",
  "lab_order",
  "missed_check_in",
  "missed_weight_log",
  // TES-751 — the item behind the coach's "Behöver återkontakt" section. Its
  // own kind rather than a reused `missed_check_in`: the card carries
  // different content (twelve weeks of cycle history, prior contacts) and a
  // different CTA, and the two mean different things to a coach reading the
  // queue.
  "needs_follow_up_contact",
  // TES-1189 — "Välkomna ny patient". One per patient; the item id is the
  // patient id.
  "coach_welcome",
  "program_change",
  "renewal",
  "onboarding",
  "side_effect",
  "medical_alert",
  // TES-986 — one medical review case.
  "medical_review",
  // TES-765 — a documented meeting without Webdoc confirmation after 48 hours.
  "webdoc_confirmation",
  // TES-1233 — a passed meeting without documentation or active handling.
  "meeting_documentation",
  // TES-1059 — a completion round whose items are all done.
  "completion_round",
  // TES-1149 — her written answer to a doctor's question.
  "doctor_question_answer",
  // TES-1203 — her own withdrawal recorded in the app. The item id is the
  // MedicalCaseOutcome id. TES-1555 retired its lane ("Avbruten vård"): no
  // item of this kind is projected any more. The kind stays because
  // WorkItemDisposition rows that closed such cards still name it.
  "case_withdrawal",
  // TES-1554 (Ö-863) — admin's task to end the subscription and refund the
  // paid days after the doctor's "not suitable" end. The item id is the
  // SubscriptionEndRefundTask id. Listed on admin's "Avslut och
  // återbetalning", never in a clinician's lane.
  "subscription_end_refund",
] as const;
export const workQueueItemKindSchema = z.enum(WORK_QUEUE_ITEM_KINDS);
export type WorkQueueItemKind = z.infer<typeof workQueueItemKindSchema>;

// TES-1189 (TES-1173 point 1, Emelie 2026-10-08) — the coach's welcome case.
// Copy verbatim from Emelie.
//
// The case is a PROJECTION, not a row created on `subscription_active`:
// "live coach-plan subscription AND no message a coach has sent in her coach
// thread". That makes it idempotent by construction (a duplicate payment
// event or webhook cannot add a second case, there is nothing to add), and
// retroactive with no backfill. It closes when the coach's first message
// exists; opening the patient or holding a draft changes neither fact.
export const COACH_WELCOME_LANE_LABEL = "Nya patienter";
export const COACH_WELCOME_TITLE = "Välkomna ny patient";
export const COACH_WELCOME_CTA = "Skriv välkomstmeddelande";
// TES-1218 (Ö-407) — the shared case for a coach-plan patient with no
// primary coach, shown to every coach and admin until someone takes her
// ("Tilldela mig") or an admin assigns her ("Tilldela coach"). The projection
// decides per patient between this title and COACH_WELCOME_TITLE.
export const NEW_PATIENT_WITHOUT_COACH_TITLE = "Ny patient saknar coach";

/**
 * TES-1189 / TES-1344 (Ö-408) — the editable welcome draft the coach chat
 * opens with.
 *
 * TES-1355 — built from the welcome template the admin publishes in
 * coach-admin (`coach_template.welcome`), passed in as `template`. With
 * nothing published, or before it has loaded, it falls back to the A-01 text
 * in code (`COACH_WELCOME_TEMPLATE_SV`), which is also what the template was
 * seeded with.
 *
 *  - "{förnamn}": the patient's first name; with no name on file the greeting
 *    reads "Hej!".
 *  - "{coachens_förnamn}": the first name of the coach writing it, who is the
 *    patient's primary coach (TES-1218): the welcome case is routed to her
 *    queue only. Never the tenant persona.
 *
 * Both go through `personFirstName` (TES-1277). The coach edits the draft
 * before sending, and nothing sends it automatically.
 */
export function coachWelcomeDraft(input: {
  coachName: string;
  patientName?: string | null;
  template?: string | null;
}): string {
  return renderCoachTemplate(
    input.template?.trim() ? input.template : COACH_WELCOME_TEMPLATE_SV,
    input,
  );
}

// A-01: "Utkastet är alltid märkt" — shown above any draft the coach has not
// yet sent.
export const COACH_DRAFT_LABEL = "Förslag – granska innan du skickar";

// Priority reuses the handoff scale so the admin has one sort order across lanes.
export const workQueuePrioritySchema = handoffPrioritySchema; // low|medium|high|urgent
export type WorkQueuePriority = z.infer<typeof workQueuePrioritySchema>;

// ---------------------------------------------------------------------------
// DTOs
// ---------------------------------------------------------------------------

// TES-1579 — the journal notes besides a care meeting's that the doctor
// copies into Webdoc by hand and ticks "Införd i Webdoc": the monthly
// review's, the prescription transfer review's, a Kompletteringsanteckning
// and "Dokumentera receptutfärdandet". Each gets the meeting's 24-hour
// reminder and 48-hour "Infört i Webdoc saknas" row (TES-765) while its
// newest final version has no receipt. The names are JournalRevision's
// `parentKind` values.
export const WEBDOC_REMINDER_PARENT_KINDS = [
  "cycle",
  "transfer_review",
  "completion_note",
  "prescription_note",
] as const;
export type WebdocReminderParentKind =
  (typeof WEBDOC_REMINDER_PARENT_KINDS)[number];

// One actionable row in a lane. `ctaPath` is a client-relative admin route the
// UI links to (e.g. `/patients/:id`, `/review-queue`) so the workspace routes
// the clinician to where the action actually lives — the workspace surfaces,
// the owning surface acts.
export const workQueueItemSchema = z.object({
  id: z.string(),
  kind: workQueueItemKindSchema,
  patientId: z.string(),
  // Who the item is about. Optional so an older API response still parses;
  // the admin falls back to the id fragment when they are absent.
  patientName: z.string().nullable().optional(),
  personalNumberLast6: z.string().optional(),
  title: z.string(),
  priority: workQueuePrioritySchema,
  dueAt: z.string().nullable(),
  ctaPath: z.string(),
  // TES-676 — medical_alert rows only: when the doctor confirmed receipt.
  // `null` = raised but nobody has confirmed seeing it yet. Optional so
  // older responses and other kinds parse unchanged.
  acknowledgedAt: z.string().nullable().optional(),
  // TES-892 (Ö-216) — when this landed in someone's queue, and whether it has
  // been sitting there for 24 STAFFED weekday hours.
  //
  // Distinct from `dueAt`, which is a deadline the item carries of its own (a
  // handoff's, a cycle's expiry). `waitingSince` is about the QUEUE: how long
  // has this gone unanswered. A row can be late with no deadline at all, and
  // most of them have none.
  //
  // Measured in staffed time, never wall-clock — 24 wall-clock hours from a
  // Friday afternoon is a Saturday, and a badge that lights up over every
  // weekend is one clinicians learn to ignore. See staffedHoursBetween.
  //
  // Both optional: an older API response parses unchanged, and `medical_alerts`
  // deliberately carries neither. That lane is already `urgent` and sits first
  // in DOCTOR_WORK_QUEUE_LANES; Ö-216 keeps acute signals on their own
  // priority track, and a lateness badge there would add nothing but noise.
  waitingSince: z.string().nullable().optional(),
  delayed: z.boolean().optional(),
  // TES-918 — when the row's SOURCE entered the state this row describes.
  //
  // Several lanes keep one id across a state change: a lab order is first a
  // "waiting for results" row and later, same id, a "results to review"
  // row. A disposition is keyed on the id, so closing the waiting row
  // used to hide the result row too — the result never reached the queue.
  // A disposition older than `stateSince` was about an earlier state and no
  // longer hides the row. Absent = the row never changes state, and any
  // disposition hides it, as before.
  //
  // Distinct from `waitingSince`, which is a queue-lateness clock and for
  // labs deliberately starts at the order for both states.
  stateSince: z.string().nullable().optional(),
  // TES-1218 (Ö-407) — `coach_welcome` rows only: true on the shared
  // "Ny patient saknar coach" case, whose actions are "Tilldela mig" and
  // "Tilldela coach" rather than the welcome message.
  needsCoach: z.boolean().optional(),
  // TES-812 — `webdoc_confirmation` rows only: WHICH consult is waiting to be
  // entered, and what its note says.
  //
  // Emelie 2026-09-19: "Infört i Webdoc saknas" does not say which
  // documentation object is to be acknowledged. The title was a constant and
  // the row carried only `patientId`, so a doctor with two documented
  // consults on the same patient could not tell which one the confirmation
  // was about.
  //
  // `noteExcerpt` is the first line of the saved assessment. The confirmation
  // is an assertion ABOUT A TEXT — "this note is in Webdoc" — and the text
  // should be visible when you assert it. It is an excerpt, not the note: the
  // card is a queue row, and the full record lives behind the CTA.
  //
  // All optional: no other kind carries them, and an older API response parses
  // unchanged.
  meetingId: z.string().uuid().optional(),
  meetingReason: z.string().optional(),
  noteExcerpt: z.string().nullable().optional(),
  // TES-1579 — `webdoc_confirmation` rows about a journal note OTHER than a
  // care meeting's: which note it is. The item id is then that parent's id
  // (the cycle, transfer review, completion round or pending first cycle),
  // and the receipt is ticked in the note's own Webdoc step behind
  // `ctaPath`, never on the row. Absent on a meeting row, which keeps
  // `meetingId` and its inline confirmation.
  journalParentKind: z.enum(WEBDOC_REMINDER_PARENT_KINDS).optional(),
  // TES-1284 — `lab_order` rows of the case's own order once she has
  // reported the sample: the Webdoc check's state, her stated sampling time,
  // the latest check and how many there have been. Optional: no other row
  // carries it, and an older API response parses unchanged.
  labResultCheck: labResultCheckSchema.optional(),
  // TES-1400 (Ö-426) — `follow_up` rows only: the lane the follow-up was set
  // to come back in. The server lists the row in that lane; the card names
  // it. Optional: no other row carries it.
  returnLane: workQueueLaneSchema.optional(),
});
export type WorkQueueItem = z.infer<typeof workQueueItemSchema>;

export const workQueueLaneResultSchema = z.object({
  lane: workQueueLaneSchema,
  items: z.array(workQueueItemSchema),
  // Present-but-empty lanes carry an optional note (e.g. a not-yet-wired
  // `medical_alerts` source) so the admin can render an honest empty state.
  note: z.string().nullable(),
});
export type WorkQueueLaneResult = z.infer<typeof workQueueLaneResultSchema>;

export const workQueueSchema = z.object({
  role: employeeRoleSchema,
  lanes: z.array(workQueueLaneResultSchema),
});
export type WorkQueue = z.infer<typeof workQueueSchema>;

// ---------------------------------------------------------------------------
// Doctor decision-loop actions (TES-290)
// ---------------------------------------------------------------------------

// Approve a patient-submitted cycle renewal. Advances the cycle to `approved`
// (spawning the next cycle) at the given dose. `needsNewLabs` routes the next
// cycle back through `pending_assessment` (labs gate) instead of straight to
// `active`. `clinicalNote` (legacy since TES-1034; the admin no longer sends
// it) is a free-text decision artifact persisted on the
// cycle (never audited as free text).
// TES-636 — the prescription is mandatory at renewal too. `dose` used to be
// optional, and the server then fell back to `cycle.dose || "0.25mg"`: an
// approval with no dose given handed the patient a constant.
//
// TES-997 (Ö-255) — every monthly review carries the same three prescription
// choices, required. Only "Nytt recept är utfärdat i Webdoc" spawns a cycle
// carrying a prescription; "Befintligt recept gäller fortfarande" continues
// the monthly rhythm on the order she already holds (with the structured
// "Befintligt recept gäller till" when known);
// "Recept kan inte utfärdas ännu" spawns nothing.
//
// TES-1034 (Emelie, point 3) — and `webdocControl.journalInWebdoc`: the
// renewal may not be completed without its journal note, even when no care
// meeting is held. The API refuses a decision without it (400
// journal_not_in_webdoc) and, with it, stores the note as it stands as its
// final version plus the doctor's Webdoc receipt, in the same transaction
// as the decision. Checked by the API rather than refined here, so the
// refusal carries its own messageKey instead of a generic validation error.
export const approveRenewalSchema = prescriptionOrderSchema.extend({
  needsNewLabs: z.boolean().optional(),
  clinicalNote: z.string().trim().max(4000).optional(),
  prescription: prescriptionDecisionSchema,
  webdocControl: webdocControlSchema.optional(),
});
export type ApproveRenewalRequest = z.infer<typeof approveRenewalSchema>;

/**
 * TES-1034 — what a monthly review decision that carries no order of its own
 * (a rejection, the feedback publish) sends for the journal note: the
 * Webdoc confirmation, and the order on screen, which the note's decision
 * box is assembled with exactly as the Webdoc step showed it. The order is
 * only read into the note; it is never written to the cycle.
 */
export const monthlyJournalConfirmationFields = {
  webdocControl: webdocControlSchema.optional(),
  journalOrder: meetingPrescriptionOrderSchema.optional(),
  // TES-1058 — and the prescription status chosen in the Webdoc step, which
  // the note's "Receptstatus" line is written from. Read only into the note;
  // only the approval records a prescription.
  journalPrescription: prescriptionDecisionSchema.optional(),
};

// Reject a renewal. Terminal for the cycle. Optional clinician reason + note.
export const rejectRenewalSchema = z.object({
  reason: z.string().trim().max(2000).optional(),
  clinicalNote: z.string().trim().max(4000).optional(),
  ...monthlyJournalConfirmationFields,
});
export type RejectRenewalRequest = z.infer<typeof rejectRenewalSchema>;

// TES-1055 (Emelie, point H) — "Nytt recept är utfärdat i Webdoc" recorded
// after the decision, on the live cycle. It stamps prescribedAt and raises
// the pickup task, so it carries the same Webdoc confirmation as every other
// publish of a prescription. The API refuses it without `journalInWebdoc`
// (400 journal_not_in_webdoc).
export const issuePrescriptionSchema = z.object({
  webdocControl: webdocControlSchema.optional(),
});
export type IssuePrescriptionRequest = z.infer<typeof issuePrescriptionSchema>;

// Set / adjust the dose on a cycle (replaces the hardcoded 0.25mg default).
// TES-636 — a dose adjustment is a new prescription, not a loose string.
//
// TES-863 (Ö-228) — `correction` is the doctor saying "this was a typo, not a
// decision", and it is the ONLY thing that separates the two. Emelie
// 2026-09-23: a correction must never reach the patient as a "dosändring",
// because, in her words:
// "en dosändring är ett kliniskt beslut med eget datum, en rättelse är ett tangentnedslag som aldrig var ett beslut".
//
// Nothing about the new values can tell the two apart — 0,5 mg typed for
// 0,25 mg looks exactly like a titration — so the distinction has to be
// asserted by the human who knows which one it was. It defaults to false:
// the overwhelmingly common case is a real adjustment, and Emelie did not ask
// for "inget förvalt" here the way Ö-221 did for the subscription plan.
export const adjustDoseSchema = prescriptionOrderSchema
  .extend({
    reason: z.string().trim().max(2000).optional(),
    correction: z.boolean().optional(),
  })
  .superRefine((v, ctx) => {
    // "rättas ... med före/efter-värde, skäl, läkare och tidpunkt i auditloggen."
    // Three of the four fall out of the write; the reason is
    // the one a human has to supply, so the edge refuses without it rather
    // than writing half an audit row. Same shape as Ö-229's goal weight.
    if (v.correction && !v.reason) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["reason"],
        message: "correction_reason_required",
      });
    }
  });
export type AdjustDoseRequest = z.infer<typeof adjustDoseSchema>;

// Attach / replace a free-form clinical note on a cycle (decision artifact).
export const cycleNoteSchema = z.object({
  note: z.string().trim().max(4000),
});
export type CycleNoteRequest = z.infer<typeof cycleNoteSchema>;

// Mark that this cycle does not require new labs before its next step.
export const markLabNotNeededSchema = z.object({
  note: z.string().trim().max(2000).optional(),
});
export type MarkLabNotNeededRequest = z.infer<typeof markLabNotNeededSchema>;

// Request a follow-up on a patient — creates a Handoff to the given role.
export const followUpRequestSchema = z.object({
  toRole: employeeRoleSchema.optional(),
  reason: z.string().trim().min(1).max(2000),
  priority: handoffPrioritySchema.default("medium"),
  context: z.string().trim().max(4000).optional(),
});
export type FollowUpRequest = z.infer<typeof followUpRequestSchema>;

// ---------------------------------------------------------------------------
// Quick handling (TES-748)
// ---------------------------------------------------------------------------

// What a clinician did with a projected row. Only recorded when the SOURCE
// cannot express it: a check-in closes through FormSubmission.reviewedAt, a
// handoff through its own status. See the model comment in schema.prisma.
export const WORK_ITEM_DISPOSITIONS = [
  "closed_with_reply",
  "closed_without_action",
  "escalated",
  "deferred",
  // TES-751 — from the package:
  // "inskickad F-04 … kan stänga ett öppet ärende som 'patienten återkom'".
  // Written by the system, not picked by a coach: it is the only
  // disposition nobody chose, and collapsing it into closed_without_action
  // would have made the history read as though a coach decided each of these
  // needed nothing.
  "patient_returned",
  // TES-1400 (Ö-410) — an admin closed an ADMINISTRATIVE work item with a
  // reason ("Stäng arbetsmoment"). Its own value so the history never reads
  // as though a clinician decided it.
  "closed_administratively",
] as const;
export const workItemDispositionSchema = z.enum(WORK_ITEM_DISPOSITIONS);
export type WorkItemDisposition = z.infer<typeof workItemDispositionSchema>;

// TES-1208 — the structured reasons for closing a case without contact. A-01
// gives one list per kind of case, verbatim:
//   work queue:        "dubblett, felaktigt ärende, redan hanterat på annat sätt
//                       eller ingen åtgärd krävs"
//   check-in:          "inget svar behövs; redan hanterat i chatten; dubblett;
//                       hanterat på annat sätt; eller annat med obligatorisk fritext"
//   follow-up contact: "Avsluta utan kontakt med strukturerad orsak. Exempel på
//                       avslutsorsak: Ingen kontakt nu – patienten är fortsatt aktiv."
// One code set across the lists, so a count over "dubblett" covers every kind.
// `other` is also what every reason recorded as free text before this read
// as: the old text stays beside it as the note.
export const CLOSE_REASON_CODES = [
  "no_reply_needed",
  "handled_in_chat",
  "duplicate",
  "wrong_case",
  "handled_otherwise",
  "no_action_needed",
  "no_contact_now_patient_active",
  "other",
] as const;
export const closeReasonCodeSchema = z.enum(CLOSE_REASON_CODES);
export type CloseReasonCode = z.infer<typeof closeReasonCodeSchema>;

export type CloseReasonOption = { code: CloseReasonCode; label: string };

// "Avsluta utan svar" on a weekly check-in.
export const CHECKIN_CLOSE_REASONS: readonly CloseReasonOption[] = [
  { code: "no_reply_needed", label: "Inget svar behövs" },
  { code: "handled_in_chat", label: "Redan hanterat i chatten" },
  { code: "duplicate", label: "Dubblett" },
  { code: "handled_otherwise", label: "Hanterat på annat sätt" },
  { code: "other", label: "Annat" },
];

// "Avsluta utan åtgärd" on any other work-queue case. A-01 says it "får
// endast användas för" these four, so there is no "Annat" to pick here.
export const WORK_ITEM_CLOSE_REASONS: readonly CloseReasonOption[] = [
  { code: "duplicate", label: "Dubblett" },
  { code: "wrong_case", label: "Felaktigt ärende" },
  { code: "handled_otherwise", label: "Redan hanterat på annat sätt" },
  { code: "no_action_needed", label: "Ingen åtgärd krävs" },
];

// "Avsluta utan kontakt" on "Behöver återkontakt". A-01 gives one example
// reason only; until Emelie says otherwise it is that example plus the
// general list (TES-1208).
export const FOLLOW_UP_CONTACT_CLOSE_REASONS: readonly CloseReasonOption[] = [
  {
    code: "no_contact_now_patient_active",
    label: "Ingen kontakt nu – patienten är fortsatt aktiv",
  },
  ...WORK_ITEM_CLOSE_REASONS,
];

/** The reasons a coach may pick when closing a case of this kind. */
export function closeReasonsFor(
  kind: WorkQueueItemKind,
): readonly CloseReasonOption[] {
  if (kind === "checkin") return CHECKIN_CLOSE_REASONS;
  if (kind === "needs_follow_up_contact")
    return FOLLOW_UP_CONTACT_CLOSE_REASONS;
  return WORK_ITEM_CLOSE_REASONS;
}

/**
 * The reason as history shows it. The kind picks the list's own wording
 * (the check-in list says "Hanterat på annat sätt", the work-queue list
 * "Redan hanterat på annat sätt"). A code outside that list (a migrated
 * free-text reason is `other` on every kind) takes its label from whichever
 * list has it.
 */
export function closeReasonLabel(
  code: CloseReasonCode,
  kind: WorkQueueItemKind = "checkin",
): string {
  const pick = (list: readonly CloseReasonOption[]) =>
    list.find((o) => o.code === code)?.label;
  return (
    pick(closeReasonsFor(kind)) ??
    pick(CHECKIN_CLOSE_REASONS) ??
    pick(FOLLOW_UP_CONTACT_CLOSE_REASONS) ??
    code
  );
}

// `reason` is required for closed_without_action and only for it — the package
// makes that reason mandatory, and a superRefine is the only way to say "this
// field's requiredness depends on that field's value" in one schema the API
// and the admin both validate against.
//
// TES-1208 — the reason is now `reasonCode` plus an optional note in
// `reason`. A close without a code still parses when it carries text: that is
// the doctor's card, which keeps its free-text reason and is stored as
// `other`. The API refuses a coach's close without a code, and checks that the
// code belongs to the case's list. `other` always needs the note.
export const disposeWorkItemSchema = z
  .object({
    // TES-1400 — `closed_administratively` is the admin's own close
    // (adminCloseWorkItemSchema); a clinician's close can never claim it.
    disposition: workItemDispositionSchema.exclude(["closed_administratively"]),
    reasonCode: closeReasonCodeSchema.optional(),
    reason: z.string().trim().max(2000).optional(),
  })
  .superRefine((value, ctx) => {
    if (value.disposition !== "closed_without_action") return;
    const hasNote = Boolean(value.reason?.trim());
    if (!value.reasonCode && !hasNote) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["reason"],
        message: "reason_required_when_closing_without_action",
      });
    }
    if (value.reasonCode === "other" && !hasNote) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["reason"],
        message: "note_required_for_other",
      });
    }
  });
export type DisposeWorkItemRequest = z.infer<typeof disposeWorkItemSchema>;

// TES-1400 (Ö-410, Emelie 2026-10-15) — "Endast behörig läkare får registrera
// slutligt medicinskt utfall. Administrativ personal kan stänga ett
// administrativt arbetsmoment om rollen har behörighet, men får aldrig ändra
// det medicinska utfallet. Handlingarna auditloggas separat."
//
// The work items an admin may close. A closed, hard-coded set: the server
// refuses every other kind, so a medical item can never be closed by admin.
// Proposed by us, for Emelie to confirm (TES-475):
//
//  - `webdoc_confirmation` — "Infört i Webdoc saknas": the journal note is
//    written and the outcome published; what is missing is the receipt that
//    it was entered in Webdoc. Closing the row does not confirm the entry
//    (`webdocConfirmedAt` stays empty) and changes nothing medical.
//  - `lab_result_escalation` — a row of the operational queue "Saknade
//    provsvar" (TES-1401, Ö-473): the remiss, sampling and transfer check.
//    Its own kind, keyed by the lab order id, and NOT the doctor's
//    `lab_order` item: closing the operational check leaves the doctor's
//    waiting item, and a result that arrives still reaches the doctor.
//
// Never in this set: anything that records or changes a medical outcome, a
// prescription or a patient-facing medical message (renewals, onboarding and
// transfer decisions, medical reviews and alerts, side effects, check-ins,
// lab results, completion rounds, doctor-question answers, meeting
// documentation), nor the coach's work.
//  - `subscription_end_refund` — TES-1554 (Ö-863, Emelie 2026-10-22):
//    "Admin verkställer avslut och återbetalning." The task asks admin to end
//    the subscription and refund in Stripe by hand; closing it records that
//    she did, with her note. It changes nothing medical: the doctor's end
//    decision is already published.
export const ADMIN_CLOSABLE_WORK_ITEM_KINDS = [
  "webdoc_confirmation",
  "lab_result_escalation",
  "subscription_end_refund",
] as const;
export const adminClosableWorkItemKindSchema = z.enum(
  ADMIN_CLOSABLE_WORK_ITEM_KINDS,
);
export type AdminClosableWorkItemKind = z.infer<
  typeof adminClosableWorkItemKindSchema
>;
export const ADMIN_CLOSE_WORK_ITEM_LABEL = "Stäng arbetsmoment";
export const ADMIN_CLOSED_WORK_ITEM_LABEL = "Stängt administrativt";

// The reason is required: the close is audited as its own action and the
// reason stays with the item's history.
export const adminCloseWorkItemSchema = z
  .object({ reason: z.string().trim().min(1).max(2000) })
  .strict();
export type AdminCloseWorkItemRequest = z.infer<
  typeof adminCloseWorkItemSchema
>;

// The expanded card's payload. Fetched per item when the card opens rather
// than folded into the list response: the list already carries PHI (name +
// personnummer shorthand, renewal answers) and this contract's own header
// warns that anything added has to be weighed as clinical data leaving its
// owning surface. Keeping it separate keeps the list lean and the blast radius
// of a queue read small.
// TES-751 — the anti-nagging ("anti-tjat") policy a coach can set on the
// card. Both fields optional and independently settable: pausing and
// agreeing a frequency are different decisions, and a coach doing one must
// not silently clear the other.
export const setFollowUpPolicySchema = z.object({
  /**
   * Pause new reminders for this many weeks. Emelie names four; the schema
   * allows 1–12 so the number stays the coach's rather than the code's, and
   * 0 explicitly lifts a pause.
   */
  pauseWeeks: z.number().int().min(0).max(12).optional(),
  /**
   * Weeks between contacts, agreed with the patient. Replaces the standard
   * rule entirely while set. `null` clears it and returns her to the default.
   */
  contactIntervalWeeks: z.number().int().min(1).max(52).nullable().optional(),
});
export type SetFollowUpPolicyInput = z.infer<typeof setFollowUpPolicySchema>;

export const followUpPolicySchema = z.object({
  pausedUntil: z.string().nullable(),
  contactIntervalWeeks: z.number().int().nullable(),
});
export type FollowUpPolicyDto = z.infer<typeof followUpPolicySchema>;

// TES-751 — what a coach needs in front of her before deciding whether to
// reach out, and deliberately nothing more.
//
// Emelie 2026-09-17 draws the line explicitly:
// "Coachen ska inte få klinisk fritext eller medicinska originalsvar genom detta kort."
// So the three statuses below are BOOLEANS. `medicalFollowUpInProgress` in
// particular says only that something is under way on the medical side —
// never what, never why. That is exactly the shape Ö-169 permits: a status,
// not content.
export const weeklyCycleOutcomeSchema = z.enum([
  "checked_in",
  "milestone_instead",
  // TES-1526 (Ö-768) — no F-04 was asked: an open F-05 held it back. Not a
  // miss, and skipped by the run rather than ending it.
  "held_back",
  "missed",
]);
export type WeeklyCycleOutcome = z.infer<typeof weeklyCycleOutcomeSchema>;

export const followUpContactContextSchema = z.object({
  /** Most recent completed F-04, or null if she has never checked in. */
  lastCheckInAt: z.string().nullable(),
  /** How many complete cycles in a row ended without one. */
  consecutiveMissedCycles: z.number().int().min(0),
  /**
   * Twelve completed weeks, newest first. An F-05 week is marked as its own
   * outcome rather than folded into "checked in": the coach should be able to
   * see that the check-in was replaced, not merely that the week was fine.
   */
  history: z.array(
    z.object({
      weekStart: z.string(),
      outcome: weeklyCycleOutcomeSchema,
    }),
  ),
  /** How many times this signal has fired for her before, ever. */
  priorSignalCount: z.number().int().min(0),
  /**
   * Previous times a coach handled this card, newest first — date and
   * outcome, which is what "datum och utfall för tidigare coachkontakter"
   * asks for. The first entry doubles as "senaste coachkontakt".
   */
  priorContacts: z.array(
    z.object({
      handledAt: z.string(),
      disposition: workItemDispositionSchema,
      employeeId: z.string(),
      // TES-1208 — why it was closed without contact, for
      // closed_without_action: the code and the coach's optional note.
      // Optional so an older API response still parses.
      reasonCode: closeReasonCodeSchema.nullable().optional(),
      reason: z.string().nullable().optional(),
    }),
  ),
  /** Neutral decision support. Booleans, never content. */
  hasActiveCareRelationship: z.boolean(),
  hasActiveSubscription: z.boolean(),
  medicalFollowUpInProgress: z.boolean(),
  /**
   * The anti-nagging policy currently in force, or null when she is on the
   * default rule. Shown so a coach can see that a colleague already paused
   * this patient rather than pausing her a second time.
   */
  policy: followUpPolicySchema.nullable(),
});
export type FollowUpContactContext = z.infer<
  typeof followUpContactContextSchema
>;

export const workQueueItemContextSchema = z.object({
  item: workQueueItemSchema,
  // What changed since the last contact — the memory area of the same name,
  // which is where that question is already answered rather than a second
  // computation of it.
  changesSinceLastContact: z.array(
    z.object({ id: z.string(), body: z.string(), observedAt: z.string() }),
  ),
  // TES-1214 — the same counts as the patient page's Aktuellt, anchored to
  // the coach's latest message. A-01: "Finns relevant historik får kortet inte samtidigt säga ”Inget noterat sedan sist” utan att förklara varför informationen inte är tillämplig."
  // With no Patientminnet change the card says since when nothing is new, or
  // that there has been no coach contact yet. Optional so an older server's
  // answer still parses.
  changesSince: coachNowSchema.shape.changes.optional(),
  // Agreements still standing. The package wants the open agreement visible at
  // the moment of replying, not a click away.
  openAgreements: z.array(
    z.object({ id: z.string(), body: z.string(), observedAt: z.string() }),
  ),
  // Follow-ups already scheduled for this patient, so a coach does not book a
  // second one for the same thing.
  openFollowUps: z.array(
    z.object({ id: z.string(), prompt: z.string(), dueAt: z.string() }),
  ),
  // Already handled? Present when a disposition exists — the card renders the
  // closed state rather than offering the actions again.
  disposition: z
    .object({
      disposition: workItemDispositionSchema,
      handledAt: z.string(),
      employeeId: z.string(),
      // TES-1208 — set for closed_without_action. Optional so an older API
      // response still parses.
      reasonCode: closeReasonCodeSchema.nullable().optional(),
    })
    .nullable(),
  // TES-751 — the "Behöver återkontakt" card. Present only for
  // `needs_follow_up_contact`; null for every other kind, so no other card
  // pays for the extra reads.
  followUpContact: followUpContactContextSchema.nullable(),
});
export type WorkQueueItemContext = z.infer<typeof workQueueItemContextSchema>;

// The sort the queue is worked in. Lives here rather than only in the admin so
// that "next prioritised item" server-side and the rendered list agree — two
// implementations of an ordering drift, and the coach would then be handed a
// different item than the one at the top of her screen.
export const WORK_QUEUE_PRIORITY_RANK: Record<WorkQueuePriority, number> = {
  urgent: 0,
  high: 1,
  medium: 2,
  low: 3,
};

export function compareWorkQueueItems(
  a: WorkQueueItem,
  b: WorkQueueItem,
): number {
  const byPriority =
    WORK_QUEUE_PRIORITY_RANK[a.priority] - WORK_QUEUE_PRIORITY_RANK[b.priority];
  if (byPriority !== 0) return byPriority;
  // Oldest due date first; items with no due date sort last, since "no
  // deadline" is not more urgent than any deadline.
  if (a.dueAt && b.dueAt) return a.dueAt.localeCompare(b.dueAt);
  if (a.dueAt) return -1;
  if (b.dueAt) return 1;
  return 0;
}

// TES-892 (Ö-216), Emelie 2026-09-23:
// "Försenad status efter 24 bemannade vardagstimmar utan svar/aktiv hantering."
// Same threshold the transfer queue uses — one definition of late across
// every surface a clinician reads.
export const WORK_QUEUE_DELAYED_STAFFED_HOURS = 24;
